const SUN_ALT = 26;
import { Sun } from "lucide-react";

export const SideView = ({ tilt, footprintFt, shadowFt, pitchFt, model, shadowSafe }) => {
  const W = 560;
  const H = 268;
  const groundY = 190;
  const k = Math.min(22, (W - 130) / (pitchFt + footprintFt));
  const F = footprintFt * k;
  const Hh = model.lenFt * Math.sin((tilt * Math.PI) / 180) * k;
  const S = shadowFt * k;
  const xB = W - 92 - F;
  const xTop = xB + F;
  const yBase = groundY - 2;
  const yTop = groundY - 2 - Hh;
  const tipX = xB - S;
  const ghostBase = shadowSafe ? xB - pitchFt * k : xB - (model.lenFt + 0.4) * k;
  const heightFt = model.lenFt * Math.sin((tilt * Math.PI) / 180);
  const sun = { x: W - 46, y: 44 };
  const rad = (SUN_ALT * Math.PI) / 180;

  const hatch = Array.from({ length: 26 }, (_, i) => 24 + i * 20);
  const midX = (xB + xTop) / 2;
  const midY = (yBase + yTop) / 2;

  return (
    <div className="rounded-2xl border border-white/10 bg-ink/60 p-5" data-testid="designer-sideview">
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-gold">
          <Sun className="h-3.5 w-3.5" /> Side view — structure &amp; shadow
        </p>
        <span className={`font-mono text-[9px] tracking-[0.15em] ${shadowSafe ? "text-emerald-300" : "text-red-300"}`} data-testid="designer-sideview-note">
          {shadowSafe ? "SHADOW CLEARS NEXT ROW" : "SHADOW HITS NEXT ROW"}
        </span>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="mt-3 w-full" role="img" aria-label={`Side view of ${tilt} degree tilted solar panel structure with ${shadowFt.toFixed(1)} feet shadow`}>
        <defs>
          <marker id="sv-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#FFB800" />
          </marker>
          <marker id="sv-arrow-dim" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#9CA3AF" />
          </marker>
        </defs>

        {/* ground / roof */}
        <line x1="20" y1={groundY} x2={W - 20} y2={groundY} stroke="#F59E0B" strokeOpacity="0.55" strokeWidth="1.6" />
        {hatch.map((x) => (
          <line key={x} x1={x} y1={groundY} x2={x - 7} y2={groundY + 8} stroke="#F59E0B" strokeOpacity="0.25" strokeWidth="1" />
        ))}

        {/* shadow band */}
        <rect x={tipX} y={groundY - 7} width={S} height="7" fill="rgba(220,60,60,0.22)" stroke="rgba(220,60,60,0.45)" strokeWidth="0.8" />
        <text x={tipX + S / 2} y={groundY - 12} textAnchor="middle" fill="#F87171" fontSize="9" fontFamily="'JetBrains Mono', monospace" letterSpacing="1">
          SHADOW {shadowFt.toFixed(1)} FT
        </text>

        {/* next row ghost */}
        <line x1={ghostBase} y1={yBase} x2={ghostBase + F} y2={yBase - Hh} stroke={shadowSafe ? "#F59E0B" : "#F87171"} strokeOpacity={shadowSafe ? 0.4 : 0.65} strokeWidth="5" strokeLinecap="round" strokeDasharray="6 5" />
        <text x={ghostBase + F / 2} y={yBase - Hh - 26} textAnchor="middle" fill={shadowSafe ? "rgba(245,158,11,0.6)" : "#F87171"} fontSize="8" fontFamily="'JetBrains Mono', monospace" letterSpacing="1">
          NEXT ROW
        </text>

        {/* mounting legs */}
        <line x1={xB + F * 0.15} y1={groundY} x2={xB + F * 0.15} y2={yBase - Hh * 0.15} stroke="#7C8598" strokeWidth="3" />
        <line x1={xB + F * 0.88} y1={groundY} x2={xB + F * 0.88} y2={yBase - Hh * 0.88} stroke="#7C8598" strokeWidth="3" />
        <rect x={xB + F * 0.15 - 5} y={groundY - 2} width="10" height="3" fill="#7C8598" />
        <rect x={xB + F * 0.88 - 5} y={groundY - 2} width="10" height="3" fill="#7C8598" />

        {/* panel */}
        <line x1={xB} y1={yBase} x2={xTop} y2={yTop} stroke="#223449" strokeWidth="8" strokeLinecap="round" />
        <line x1={xB} y1={yBase} x2={xTop} y2={yTop} stroke="#FFB800" strokeWidth="1.2" strokeLinecap="round" />
        <text
          x={midX}
          y={midY - 7}
          textAnchor="middle"
          fill="#F3F4F6"
          fontSize="9"
          fontFamily="'JetBrains Mono', monospace"
          transform={`rotate(${-tilt}, ${midX}, ${midY})`}
        >
          PANEL {model.lenFt} FT
        </text>

        {/* tilt arc */}
        <path d={`M ${xB + 34} ${yBase} A 34 34 0 0 0 ${xB + 34 * Math.cos((tilt * Math.PI) / 180)} ${yBase - 34 * Math.sin((tilt * Math.PI) / 180)}`} fill="none" stroke="#FFB800" strokeWidth="1" />
        <text x={xB + 42} y={yBase - 12} fill="#FFB800" fontSize="10" fontFamily="'JetBrains Mono', monospace">
          {tilt}°
        </text>

        {/* sun ray */}
        <line x1={sun.x} y1={sun.y} x2={xTop} y2={yTop} stroke="#FFB800" strokeWidth="1.4" markerEnd="url(#sv-arrow)" opacity="0.9" />
        <line x1={xTop} y1={yTop} x2={tipX + 2} y2={groundY - 4} stroke="#F87171" strokeWidth="1.2" strokeDasharray="5 4" opacity="0.8" />
        <circle cx={sun.x} cy={sun.y} r="7" fill="#FFB800" />
        {Array.from({ length: 8 }, (_, i) => {
          const a = (i * Math.PI) / 4;
          return (
            <line key={i} x1={sun.x + Math.cos(a) * 10} y1={sun.y + Math.sin(a) * 10} x2={sun.x + Math.cos(a) * 14} y2={sun.y + Math.sin(a) * 14} stroke="#FFB800" strokeWidth="1.6" />
          );
        })}
        <text x={sun.x - 12} y={sun.y - 12} textAnchor="end" fill="rgba(243,244,246,0.7)" fontSize="8" fontFamily="'JetBrains Mono', monospace" letterSpacing="1">
          WINTER 9 AM SUN — {SUN_ALT}°
        </text>

        {/* height dim */}
        <line x1={xTop + 14} y1={groundY} x2={xTop + 14} y2={yTop} stroke="#9CA3AF" strokeWidth="0.8" markerStart="url(#sv-arrow-dim)" markerEnd="url(#sv-arrow-dim)" />
        <text x={xTop + 19} y={(groundY + yTop) / 2 + 3} fill="#9CA3AF" fontSize="9" fontFamily="'JetBrains Mono', monospace">
          {heightFt.toFixed(1)} ft
        </text>

        {/* pitch dim */}
        <line x1={ghostBase} y1={groundY + 34} x2={xB} y2={groundY + 34} stroke="#9CA3AF" strokeWidth="0.8" markerStart="url(#sv-arrow-dim)" markerEnd="url(#sv-arrow-dim)" />
        <text x={(ghostBase + xB) / 2} y={groundY + 47} textAnchor="middle" fill="#9CA3AF" fontSize="9" fontFamily="'JetBrains Mono', monospace">
          ROW PITCH {pitchFt.toFixed(1)} FT
        </text>
      </svg>

      <p className="mt-2 text-[11px] leading-relaxed text-mutegray">
        {shadowSafe
          ? `Structure holds panels at ${tilt}° with ${pitchFt.toFixed(1)} ft row pitch — the winter 9 AM shadow (${shadowFt.toFixed(1)} ft) never reaches the next row, so every panel produces at full power.`
          : `At this dense spacing the ${shadowFt.toFixed(1)} ft shadow reaches the next row at ${tilt}° tilt — expect shading losses around winter mornings. We only recommend this for flush-mounted layouts.`}
      </p>
    </div>
  );
};

export default SideView;
